import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { getPoojas, createPooja, updatePooja, deletePooja, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill, BtnPrimary, BtnGhost, Field, inputCls, areaCls } from "./_ui";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const CATS = ["Home", "Festival", "Shanti", "Family"];
const EMPTY = { name: "", slug: "", description: "", duration_text: "", price: "", category: "Home", image_url: "" };
const kebab = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function PoojaForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const body = {
      name: f.name.trim(), description: f.description, duration_text: f.duration_text,
      category: f.category, image_url: f.image_url || null, price: f.price ? Number(f.price) : 0,
    };
    setBusy(true);
    try {
      if (f.id) await updatePooja(f.id, body);
      else await createPooja({ ...body, slug: f.slug.trim() || kebab(f.name) });
      toast.success(f.id ? "Pooja updated" : "Pooja added");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={f.id ? "Edit pooja" : "Add a pooja"} onClose={onClose} testid="pooja-form-modal"
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary onClick={save} disabled={busy} data-testid="pooja-form-save">{busy ? "Saving…" : "Save"}</BtnPrimary></>}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" full><input value={f.name} onChange={set("name")} data-testid="pooja-field-name" className={inputCls} /></Field>
        {!f.id && <Field label="Slug (auto if blank)" full><input value={f.slug} onChange={set("slug")} placeholder={kebab(f.name) || "e.g. satyanarayan-pooja"} className={inputCls} /></Field>}
        <Field label="Duration (e.g. 2 hr)"><input value={f.duration_text} onChange={set("duration_text")} className={inputCls} /></Field>
        <Field label="Starting price (₹)"><input type="number" value={f.price} onChange={set("price")} data-testid="pooja-field-price" className={inputCls} /></Field>
        <Field label="Category">
          <select value={f.category} onChange={set("category")} className={inputCls}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label="Image URL"><input value={f.image_url} onChange={set("image_url")} placeholder="/pooja-art/..." className={inputCls} /></Field>
        <Field label="Description" full><textarea rows={3} value={f.description} onChange={set("description")} className={areaCls} /></Field>
      </div>
    </Modal>
  );
}

export default function PujaCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = () => getPoojas().then(setRows).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };

  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-puja-catalog">
      <PageHead eyebrow="Service catalogue" title="Puja catalog"
        desc="Manage every pooja, homa and ceremony offered on the marketplace — pricing, category and visibility."
        action={<BtnPrimary onClick={() => setForm(EMPTY)} data-testid="pooja-add-btn" className="inline-flex items-center gap-1.5"><Plus size={16} /> Add pooja</BtnPrimary>} />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Total poojas" value={rows.length} />
        <Stat tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Active" value={activeCount} />
        <Stat tint={{ bg: "#F3EEEA", fg: "#8A8072" }} label="Hidden" value={rows.length - activeCount} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((p) => (
          <div key={p.id} data-testid={`pooja-card-${p.id}`} className="overflow-hidden rounded-2xl border border-[#E9E2D6] bg-white">
            <div className="h-36 w-full bg-[#F5F1E8]">
              {p.image_url && <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-base font-semibold text-[#2C241B]">{p.name}</h3>
                <Pill status={p.active ? "active" : "draft"} />
              </div>
              <p className="mt-1 font-body text-xs text-[#8A8072]">{p.category} · {p.duration_text || "—"} · from {inr(p.price)}</p>
              <p className="mt-2 line-clamp-2 font-body text-xs text-[#A79C8A]">{p.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => setForm({ ...EMPTY, ...p, price: p.price ?? "" })} data-testid={`pooja-edit-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]"><Pencil size={13} /> Edit</button>
                <button onClick={() => act(() => updatePooja(p.id, { active: !p.active }), p.active ? "Hidden" : "Activated")} data-testid={`pooja-toggle-${p.id}`} className="inline-flex items-center gap-1 rounded-lg border border-[#E5DFD3] px-2.5 py-1.5 font-body text-xs font-medium text-[#5C5449] hover:bg-[#F5F1E8]">{p.active ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}</button>
                <button onClick={() => { if (window.confirm(`Delete ${p.name}?`)) act(() => deletePooja(p.id), "Pooja deleted"); }} data-testid={`pooja-delete-${p.id}`} className="inline-flex items-center gap-1 rounded-lg bg-[#FDECEA] px-2.5 py-1.5 font-body text-xs font-semibold text-[#C62828] hover:bg-[#f8dcd8]"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!loading && rows.length === 0 && <div className="mt-6"><Empty>No poojas yet — add your first one.</Empty></div>}

      {form && <PoojaForm initial={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
